import type { ReactNode } from 'react';
import { usePermission } from '@/hooks/usePermission';

interface RequirePermissionProps {
  /** Require ANY of these codes. */
  any?: readonly string[];
  /** Require ALL of these codes. */
  all?: readonly string[];
  /** Rendered when the permission check fails. Defaults to `null` (hide). */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Render `children` only when the current user satisfies the permission
 * requirement. The default `fallback` is `null` so unauthorized actions are
 * hidden entirely (not just disabled) — operators should not be teased with
 * buttons they cannot click.
 *
 * If neither `any` nor `all` is provided, the children render unconditionally.
 * When both are provided, both must be satisfied.
 */
export function RequirePermission({ any, all, fallback = null, children }: RequirePermissionProps) {
  const { hasAny, hasAll } = usePermission();
  const allowed = (any ? hasAny(any) : true) && (all ? hasAll(all) : true);
  return <>{allowed ? children : fallback}</>;
}
